import React from 'react'
import { observer } from 'mobx-react'
import BookkeepingProgressionViewModel, {
  BookkeepingStep,
  BookkeepingStepInfo,
} from './BookkeepingProgressionViewModel'
import {
  Card,
  CardContent,
  CardContentDescription,
  CardHeader,
  CardTitle,
} from 'components/Card'
import Button from 'components/Button'
import ProgressBar from 'components/ProgressBar'
import Tooltip from 'components/Tooltip'
import Icon from 'components/Icon'

interface BookkeepingProgressionCardProps {
  vm: BookkeepingProgressionViewModel
}

export default observer(function BookkeepingProgressionCard({
  vm,
}: BookkeepingProgressionCardProps) {
  return (
    vm.bookkeepingStepInfo && (
      <Card>
        <BookkeepingProgressionCardContent
          bookkeepingStep={vm.bookkeepingStep}
          bookkeepingStepInfo={vm.bookkeepingStepInfo}
        />
      </Card>
    )
  )
})

interface BookkeepingProgressionCardContentProps {
  bookkeepingStep: BookkeepingStep
  bookkeepingStepInfo: BookkeepingStepInfo
}

const BookkeepingProgressionCardContent = observer(
  function BookkeepingProgressionCardContent({
    bookkeepingStep,
    bookkeepingStepInfo,
  }: BookkeepingProgressionCardContentProps) {
    return (
      <>
        <CardHeader>
          <CardTitle>
            {bookkeepingStepInfo.title}&nbsp;
            {bookkeepingStepInfo.tooltip && (
              <Tooltip content={bookkeepingStepInfo.tooltip}>
                <Icon size={14} name="info-blue" color="var(--blue400)" />
              </Tooltip>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ProgressBar
            key={1}
            bars={[
              {
                progress: bookkeepingStepInfo.percentDone,
              },
              {
                progress: bookkeepingStepInfo.percentPending,
                style: 'striped',
              },
            ]}
          />
        </CardContent>
        <CardContent>
          <CardContentDescription>
            {bookkeepingStepInfo.description}
          </CardContentDescription>
        </CardContent>
        {bookkeepingStepInfo.secondaryAction && (
          <CardContent compactBottomPadding>
            <Button
              type="default"
              size="wide-slim"
              onClick={bookkeepingStepInfo.secondaryAction}
              data-test={getSecondaryBookkeepingActionButtonTestId(
                bookkeepingStep,
              )}
            >
              {bookkeepingStepInfo.secondaryActionText}
            </Button>
          </CardContent>
        )}
        {bookkeepingStepInfo.primaryAction && (
          <CardContent>
            <Button
              type="primary"
              onClick={bookkeepingStepInfo.primaryAction}
              size="wide-slim"
              data-test={getPrimaryBookkeepingActionButtonTestId(
                bookkeepingStep,
              )}
            >
              {bookkeepingStepInfo.primaryActionText}
            </Button>
          </CardContent>
        )}
      </>
    )
  },
)

function getPrimaryBookkeepingActionButtonTestId(step: BookkeepingStep) {
  switch (step) {
    case BookkeepingStep.UnmanagedConfirmAccess:
      return 'confirm-unmanaged-access-button'
    case BookkeepingStep.UnmanagedGrantAccess:
      return 'grant-bookkeeping-access-button'
    case BookkeepingStep.StartBookkeeping:
      return 'start-bookkeeping-button'
    case BookkeepingStep.BookkeepingInProgress:
      return 'complete-cycle-button'
    case BookkeepingStep.StartNextCycle:
      return 'start-next-cycle-button'
    case BookkeepingStep.BookkeepingCompleted:
      return 'complete-bookkeeping-job-button'
    default:
      return undefined
  }
}

function getSecondaryBookkeepingActionButtonTestId(step: BookkeepingStep) {
  switch (step) {
    case BookkeepingStep.BookkeepingCompleted:
      return 'add-scope-change-button'
    default:
      return undefined
  }
}
